/**
 * The shell filter vocabulary that still has users: the one field treatment ({@link FIELD_CLASS}) and
 * the option and choice shapes ({@link FilterOption}, {@link FilterChoice}).
 *
 * Plan 2026-10-22-console-cleanup-g3 deleted the shell filter controls (`FilterSection`, `FilterGroup`,
 * `FilterSearch`, `FilterToggle`, `FilterSelect`, `FilterMultiSelect`), `resolveChoice` and
 * `list-filters.ts`. None had a production importer. The console pages filter with the WARD controls
 * in `src/ward/`.
 */

/**
 * ⛔ **ONE field treatment for every text/select control.** `ActionMenu` uses it.
 *
 * The corpus had two backgrounds (`bg-surface` in `DataPlanesPane`, `bg-surface-elevated` in the two log
 * surfaces) and two focus offsets (`outline-offset-1` and `outline-offset-2`). Inside one bordered box
 * those differences are not subtle — two fields side by side at different heights is the first thing the
 * eye finds.
 *
 * ⚠️ **`bg-surface-elevated`.** The deleted filter section was `bg-surface-muted`, and a field on it needed
 * the lighter fill to read as a control. The one live user, the `ActionMenu` search frame, is also
 * `bg-surface-elevated`, so there the `border-border-subtle` outline marks the field.
 */
export const FIELD_CLASS =
  'rounded border border-border-subtle bg-surface-elevated px-2 py-1 text-xs normal-case tracking-normal text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

/**
 * One option of a multi-choice control. The machine-type editors and the data location model use it as
 * their option shape.
 */
export interface FilterOption<V extends string> {
  /** ⛔ The WIRE value. Never the display form — renaming it changes what reaches the API. */
  readonly value: V;
  /** The display form. Title-case or upper-case it here; `value` stays as the server knows it. */
  readonly label: string;
  /**
   * Renders the label muted, for an option that is selectable but describes something inactive (a
   * withdrawn machine type, a region outside the provider list).
   *
   * ⚠️ ⛔ **`muted` is a SECOND channel, never the only one.** It must accompany a fact stated in words —
   * for a withdrawn machine type, the hint says "no longer offered in this region". A mute alone would
   * give the distinction by colour, which is the defect class this package bans everywhere else.
   */
  readonly muted?: boolean;
  /** Rendered after the label, e.g. a match count. `undefined` shows nothing rather than `(0)`. */
  readonly hint?: string;
  /**
   * Make the option **unselectable**, with the reason that has to travel with it.
   *
   * ## ⛔⛔ THE REASON IS REQUIRED BY THE TYPE, AND THAT IS THE WHOLE POINT OF THE SHAPE
   *
   * `disabled?: boolean` was the obvious spelling and it is the wrong one: it permits a disabled
   * control with no explanation, which is **indistinguishable from a broken one** — this library's
   * standing rule, stated at length in `ButtonLink`'s docblock and in `FormField`'s
   * never-a-silently-disabled-submit fence. Making it `{ reason }` means the explanation cannot be
   * omitted **or** forgotten at a call site, because there is no way to express the disabled state
   * without it.
   *
   * ## ⚠️ DISTINCT FROM {@link FilterOption.muted}, AND THE TWO MUST NOT BE CONFLATED
   *
   * | | selectable? | means |
   * |---|---|---|
   * | `muted` | **yes** | selectable but describes something inactive (a withdrawn machine type) |
   * | `disabled` | **no** | this option cannot be chosen right now, and here is why |
   *
   * ⛔ A muted option that should have been disabled lets the operator pick something the server will
   * refuse; a disabled option that should have been muted removes a legitimate choice. They look
   * similar on screen and are opposite facts.
   *
   * ## ⛔ NEVER HIDE INSTEAD
   *
   * Hiding an unavailable option makes the set look smaller than it is, and the operator cannot tell a
   * choice they *may not* make from one that does not exist. Disabled-with-a-reason is what lets them
   * act on it (here: scale the pool to zero first, then disable the type).
   *
   * ⭐ First adopter: 06c G6(B)'s enabled-machine-types editor, where a type still `inUse` with
   * `liveCount > 0` is refused by the producer with a **409** — so the screen states that up front
   * instead of surfacing a generic failure after the fact.
   */
  readonly disabled?: { readonly reason: string };
}

/** One choice of a single-choice filter. ⛔ `value` is the WIRE value, `label` the display form. */
export interface FilterChoice<V extends string | number> {
  readonly value: V;
  readonly label: string;
}
